import { Collapse, createStyles, withStyles } from '@material-ui/core';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect, useLocation } from 'react-router';
import { HOME_URL } from '../../../const/routes';
import CommonContainer from '../../common/CommonContainer';
import CommonLink from '../../common/CommonLink';
import CommonLogo from '../../common/CommonLogo';
import LoginForm from './LoginForm';
import LanguageSelect from '../../application/LanguageSelect';
import DarkModeSwitch from '../../application/DarkModeSwitch';
import { bindActionCreators, compose } from 'redux';
import { login } from '../../../actions/auth-actions';
import { connect } from 'react-redux';
import AppVersionWatermark from '../../application/AppVersionWatermark';
import { ReactComponent as Circle } from '../../../assets/circles.svg';
import { ReactComponent as Dots } from '../../../assets/dots.svg';
import Box from '@material-ui/core/Box';
import {
  ALMOST_WHITE_COLOR,
  BACKGROUND_DARK,
  BLACK_COLOR,
  LOGIN_DARK_BOX_SHADOW,
  LOGIN_LIGHT_BOX_SHADOW,
} from '../../../const/color-const';
import PropTypes from 'prop-types';

const DARK_SEPARATOR = '#2c3344';
const LOGIN_HEIGHT = '710px';
const RADIUS = 16;

const styles = createStyles(theme => ({
  loginPageWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignSelf: 'center',
    padding: 0,
    margin: 0,
    [theme.breakpoints.up('md')]: {
      maxWidth: '1150px',
    },
  },
  loginPageComp: {
    display: 'flex',
    flexDirection: 'row',
    borderRadius: RADIUS,
    height: '100vh',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      height: LOGIN_HEIGHT,
      boxShadow: theme.palette.type === 'light' ? LOGIN_LIGHT_BOX_SHADOW : LOGIN_DARK_BOX_SHADOW,
    },
  },
  loginSection: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    borderTopLeftRadius: RADIUS,
    borderBottomLeftRadius: RADIUS,
    borderRight: theme.palette.type === 'light' ? 'none' : `1px solid ${DARK_SEPARATOR}`,
    backgroundColor: theme.palette.type === 'light' ? 'white' : BACKGROUND_DARK,
    [theme.breakpoints.up('md')]: {
      maxWidth: '39%',
      justifyContent: 'center',
    },
  },
  textSection: {
    display: 'none',
    justifyContent: 'center',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    borderTopRightRadius: RADIUS,
    borderBottomRightRadius: RADIUS,
    padding: theme.spacing(6, 6, 6, 10),
    backgroundColor: theme.palette.type === 'light' ? '#f7f7f9' : BACKGROUND_DARK,
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  loginHeader: {
    lineHeight: '48px',
    fontSize: 48,
  },
  loginText: {
    textAlign: 'justify',
    maxWidth: '92%',
    fontSize: 16,
    zIndex: 1,
    margin: theme.spacing(4, 0, 4, 0),
  },
  circleStyling: {
    position: 'absolute',
    opacity: 0.02,
    left: 0,
    right: 0,
  },
  dotStyling: {
    position: 'absolute',
    opacity: 0.1,
    right: -5,
    top: 10,
  },
  toolbarItem: {
    margin: `0 ${theme.spacing(3)}px ${theme.spacing(2)}px ${theme.spacing(3)}px`,
    alignItems: 'center',
    display: 'flex',
    '& div': {
      color: theme.palette.type === 'light' ? BLACK_COLOR : ALMOST_WHITE_COLOR,
    },
  },
  logo: {
    maxWidth: 140,
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      maxWidth: 150,
    },
  },
  form: {
    marginTop: theme.spacing(3),
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-evenly',
    marginTop: theme.spacing(3),
  },
}));

const LoginPage = React.forwardRef((props, ref) => {
  const { classes, login, authenticated, user } = props;
  const location = useLocation();
  const fromLocation = location && location.state && location.state.from;
  
  return (
    <CommonContainer
      ref={ref}
      vAlign="center"
      className={classes.loginPageWrapper}
    >
      <div className={classes.loginPageComp}>
        <div className={classes.loginSection}>
  
          <Box display="flex">
            <div className={classes.toolbarItem}>
              <LanguageSelect />
            </div>
    
            <div className={classes.toolbarItem}>
              <DarkModeSwitch />
            </div>
          </Box>
  
          <CommonLogo className={classes.logo} />
  
          <Collapse
            in
            appear
            classes={{ wrapper: classes.form }}
          >
            <LoginForm
              onSubmit={login.bind(this)}
            />
  
  
          </Collapse>
  
          {authenticated && user && (
            <Redirect to={fromLocation || HOME_URL} />
          )}
        </div>
        <div className={classes.textSection}>
          <Circle className={classes.circleStyling} />
          <Dots className={classes.dotStyling} />
  
          <h1 className={classes.loginHeader}><FormattedMessage
            id="page.about.header"
            defaultMessage="Welcome to dinkassa.se"
          />
          </h1>
  
          <p className={classes.loginText}>
            <FormattedMessage
              id="page.about.paragraph.1"
              defaultMessage="Dinkassa.se is an online service that can be used by anyone who have {link1} from {link2}. Here you can log in and see online what's going on in your business. The service works for all types of businesses, e.g. restaurants, cafes, hairdressers and all kinds of shops."
              values={{
                link1: <CommonLink to="http://www.eskassa.se/">ES Kassasystem</CommonLink>,
                link2: <CommonLink to="http://www.erpato.se/">Erpato Europe AB</CommonLink>,
              }}
            />
          </p>
        </div>
      </div>
      <AppVersionWatermark />
    </CommonContainer>
  );
  
});

LoginPage.propTypes = {
  classes: PropTypes.object,
  login: PropTypes.func,
  authenticated: PropTypes.bool,
  user: PropTypes.object,
};

const decorate = compose(
  withStyles(styles),
  connect(
    store => ({
      authenticated: store.auth.authenticated,
      user: store.user.profile,
    }),
    dispatch => bindActionCreators({ login }, dispatch)),
);

export default decorate(LoginPage);